import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ExternalLink, Copy, Check, RefreshCw, Save, Eye, Smartphone, Monitor, Tablet,
  BarChart3, Globe,
} from "lucide-react";

const SECTION_LABELS = {
  hero: "Hero (cabeçalho)",
  logos: "Logos de clientes",
  modules: "Módulos",
  features: "Funcionalidades detalhadas",
  benefits: "Benefícios",
  differentials: "Diferenciais",
  pricing_cta: "CTA de Preço/Teste",
  faq: "Dúvidas frequentes",
  footer_cta: "Rodapé",
};

const DEVICE_ICON = { mobile: Smartphone, tablet: Tablet, desktop: Monitor, unknown: Globe };

export default function LandingAdmin() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [days, setDays] = useState(30);
  const [copied, setCopied] = useState(false);

  const landingUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/landingpage`;
  }, []);

  const load = async () => {
    const r = await api.get("/public/landing-config");
    setCfg(r.data);
  };
  const loadMetrics = async () => {
    try {
      const r = await api.get("/master/landing-metrics", { params: { days } });
      setMetrics(r.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadMetrics(); /* eslint-disable-next-line */ }, [days]);

  const update = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));
  const toggleSection = (key) => setCfg(prev => ({
    ...prev,
    sections: { ...(prev.sections || {}), [key]: !(prev.sections?.[key]) }
  }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/master/landing-config", cfg);
      setCfg(r.data);
      toast.success("Landing atualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(landingUrl); setCopied(true); toast.success("Link copiado!"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Não foi possível copiar"); }
  };

  if (!cfg) return <div className="p-8 text-sm text-neutral-500">Carregando…</div>;

  const totalClicks = (metrics?.by_type?.click_whatsapp || 0)
    + (metrics?.by_type?.click_trial || 0)
    + (metrics?.by_type?.click_buy || 0)
    + (metrics?.by_type?.click_login || 0)
    + (metrics?.by_type?.click_other || 0);
  const conversionRate = (metrics?.unique_visitors || 0) > 0
    ? ((totalClicks / metrics.unique_visitors) * 100).toFixed(1)
    : "0.0";

  return (
    <div className="space-y-8" data-testid="landing-admin-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-tiny mb-2">Marketing</p>
          <h1 className="font-display text-4xl tracking-tight">Landing Page</h1>
          <p className="text-sm text-neutral-500 mt-1">Controle tudo o que aparece em <code className="text-xs bg-neutral-100 dark:bg-white/5 rounded px-1.5 py-0.5">/landingpage</code>.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={copyLink} data-testid="copy-landing-link" className="h-9 text-xs">
            {copied ? <Check size={14} className="mr-1.5"/> : <Copy size={14} className="mr-1.5"/>} Copiar link
          </Button>
          <Button variant="outline" onClick={() => window.open(landingUrl, "_blank", "noopener,noreferrer")} className="h-9 text-xs" data-testid="open-landing-link">
            <ExternalLink size={14} className="mr-1.5"/> Abrir landing
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#0A0A0A] hover:bg-black text-white h-9 text-xs" data-testid="save-landing-config">
            <Save size={14} className="mr-1.5"/> {saving ? "Salvando…" : "Salvar alterações"}
          </Button>
        </div>
      </div>

      {/* Toggle global */}
      <Card className="p-5 border-[#E5E5E5] shadow-none flex items-center justify-between gap-4">
        <div>
          <h3 className="font-display text-base">Landing ativa?</h3>
          <p className="text-xs text-neutral-500 mt-1">Quando desligada, qualquer visitante vê uma mensagem suave de indisponibilidade — sem 404.</p>
        </div>
        <Switch checked={cfg.enabled !== false} onCheckedChange={(v) => update("enabled", v)} data-testid="toggle-landing-enabled"/>
      </Card>

      <Tabs defaultValue="content">
        <TabsList>
          <TabsTrigger value="content" data-testid="tab-landing-content">Conteúdo</TabsTrigger>
          <TabsTrigger value="cta" data-testid="tab-landing-cta">Botões e CTAs</TabsTrigger>
          <TabsTrigger value="sections" data-testid="tab-landing-sections">Seções</TabsTrigger>
          <TabsTrigger value="style" data-testid="tab-landing-style">Estilo</TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-landing-seo">SEO</TabsTrigger>
          <TabsTrigger value="metrics" data-testid="tab-landing-metrics">Métricas</TabsTrigger>
        </TabsList>

        {/* CONTEÚDO */}
        <TabsContent value="content" className="mt-6 space-y-4">
          <Card className="p-6 border-[#E5E5E5] shadow-none space-y-4">
            <div><Label>Nome da marca</Label><Input value={cfg.brand_name || ""} onChange={e=>update("brand_name", e.target.value)} data-testid="landing-brand"/></div>
            <div><Label>Tagline (linha curta de identidade)</Label><Input value={cfg.tagline || ""} onChange={e=>update("tagline", e.target.value)} data-testid="landing-tagline"/></div>
            <div>
              <Label>Título principal do Hero</Label>
              <Textarea rows={3} value={cfg.hero_title || ""} onChange={e=>update("hero_title", e.target.value)} data-testid="landing-hero-title"/>
            </div>
            <div>
              <Label>Subtítulo do Hero</Label>
              <Textarea rows={3} value={cfg.hero_subtitle || ""} onChange={e=>update("hero_subtitle", e.target.value)} data-testid="landing-hero-subtitle"/>
            </div>
          </Card>
        </TabsContent>

        {/* CTA */}
        <TabsContent value="cta" className="mt-6 space-y-4">
          <Card className="p-6 border-[#E5E5E5] shadow-none space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Texto do botão "Teste grátis"</Label><Input value={cfg.hero_cta_primary || ""} onChange={e=>update("hero_cta_primary", e.target.value)} data-testid="landing-cta-primary"/></div>
              <div><Label>Texto do botão "Saiba mais"</Label><Input value={cfg.hero_cta_secondary || ""} onChange={e=>update("hero_cta_secondary", e.target.value)} data-testid="landing-cta-secondary"/></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Número de WhatsApp (com DDI/DDD)</Label>
                <Input value={cfg.whatsapp_number || ""} onChange={e=>update("whatsapp_number", e.target.value.replace(/\D/g, ""))} placeholder="5511999999999" data-testid="landing-whatsapp-number"/>
                <p className="text-[11px] text-neutral-400 mt-1">Ex.: 5511999999999 (Brasil + DDD + número, sem espaço)</p>
              </div>
              <div>
                <Label>Mensagem padrão pro WhatsApp</Label>
                <Input value={cfg.whatsapp_message || ""} onChange={e=>update("whatsapp_message", e.target.value)} data-testid="landing-whatsapp-message"/>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label>URL do teste grátis</Label>
                <Input value={cfg.trial_url || ""} onChange={e=>update("trial_url", e.target.value)} placeholder="/login" data-testid="landing-trial-url"/>
              </div>
              <div>
                <Label>URL "Já é usuário? Entrar"</Label>
                <Input value={cfg.login_url || ""} onChange={e=>update("login_url", e.target.value)} placeholder="/login" data-testid="landing-login-url"/>
              </div>
              <div>
                <Label>URL "Comprar" (opcional)</Label>
                <Input value={cfg.buy_url || ""} onChange={e=>update("buy_url", e.target.value)} placeholder="https://pay.hotmart.com/..." data-testid="landing-buy-url"/>
              </div>
            </div>
            <p className="text-xs text-neutral-500">Se "Comprar" estiver vazio, o botão "Comprar agora" fica oculto e o fluxo cai pro WhatsApp.</p>
          </Card>
        </TabsContent>

        {/* SEÇÕES */}
        <TabsContent value="sections" className="mt-6 space-y-4">
          <Card className="p-6 border-[#E5E5E5] shadow-none">
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.entries(SECTION_LABELS).map(([k, label]) => (
                <div key={k} className="flex items-center justify-between p-3 rounded-md border border-[#E5E5E5]">
                  <span className="text-sm">{label}</span>
                  <Switch
                    checked={cfg.sections?.[k] !== false}
                    onCheckedChange={() => toggleSection(k)}
                    data-testid={`section-toggle-${k}`}
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-neutral-500 mt-4">Desligar uma seção remove ela da landing imediatamente após salvar — sem perder o conteúdo configurado.</p>
          </Card>
        </TabsContent>

        {/* ESTILO */}
        <TabsContent value="style" className="mt-6 space-y-4">
          <Card className="p-6 border-[#E5E5E5] shadow-none space-y-4">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <Label>Cor primária (verde de gestão)</Label>
                <div className="flex items-center gap-3 mt-1">
                  <input type="color" value={cfg.primary_color || "#10B981"} onChange={e=>update("primary_color", e.target.value)} className="w-12 h-10 rounded border border-[#E5E5E5] cursor-pointer" data-testid="landing-primary-color"/>
                  <Input value={cfg.primary_color || ""} onChange={e=>update("primary_color", e.target.value)} className="flex-1 font-mono"/>
                </div>
              </div>
              <div>
                <Label>Cor de destaque (azul de tecnologia)</Label>
                <div className="flex items-center gap-3 mt-1">
                  <input type="color" value={cfg.accent_color || "#0EA5E9"} onChange={e=>update("accent_color", e.target.value)} className="w-12 h-10 rounded border border-[#E5E5E5] cursor-pointer" data-testid="landing-accent-color"/>
                  <Input value={cfg.accent_color || ""} onChange={e=>update("accent_color", e.target.value)} className="flex-1 font-mono"/>
                </div>
              </div>
            </div>
            <div>
              <Label>Embed de vídeo (YouTube ou Vimeo — opcional)</Label>
              <Input value={cfg.video_url || ""} onChange={e=>update("video_url", e.target.value)} placeholder="https://youtube.com/embed/..." data-testid="landing-video-url"/>
              <p className="text-[11px] text-neutral-400 mt-1">Reservado para uso futuro. Por enquanto a landing usa só ícones e tipografia.</p>
            </div>
          </Card>
        </TabsContent>

        {/* SEO */}
        <TabsContent value="seo" className="mt-6 space-y-4">
          <Card className="p-6 border-[#E5E5E5] shadow-none space-y-4">
            <div>
              <Label>Título SEO (aparece no Google)</Label>
              <Input value={cfg.seo_title || ""} onChange={e=>update("seo_title", e.target.value)} maxLength={80} data-testid="landing-seo-title"/>
              <p className="text-[11px] text-neutral-400 mt-1">{(cfg.seo_title || "").length}/80 caracteres recomendados</p>
            </div>
            <div>
              <Label>Descrição SEO</Label>
              <Textarea rows={3} value={cfg.seo_description || ""} onChange={e=>update("seo_description", e.target.value)} maxLength={170} data-testid="landing-seo-desc"/>
              <p className="text-[11px] text-neutral-400 mt-1">{(cfg.seo_description || "").length}/170 caracteres recomendados</p>
            </div>
            <div className="rounded-lg border border-[#E5E5E5] p-4 bg-neutral-50 dark:bg-white/[0.02]">
              <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Prévia no Google</p>
              <div className="text-emerald-700 dark:text-emerald-400 text-sm hover:underline cursor-pointer">{cfg.seo_title}</div>
              <div className="text-[11px] text-neutral-500 mt-0.5">{landingUrl}</div>
              <div className="text-xs text-neutral-700 dark:text-neutral-300 mt-1 leading-snug">{cfg.seo_description}</div>
            </div>
          </Card>
        </TabsContent>

        {/* MÉTRICAS */}
        <TabsContent value="metrics" className="mt-6 space-y-4">
          <Card className="p-6 border-[#E5E5E5] shadow-none">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-display text-lg">Últimos {days} dias</h3>
              <div className="flex items-center gap-2">
                {[7, 30, 90].map(d => (
                  <Button key={d} size="sm" variant={days === d ? "default" : "outline"} className={days === d ? "bg-[#0A0A0A] text-white" : ""} onClick={() => setDays(d)} data-testid={`metrics-days-${d}`}>
                    {d}d
                  </Button>
                ))}
                <Button size="sm" variant="ghost" onClick={loadMetrics} title="Atualizar"><RefreshCw size={14}/></Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-neutral-50 dark:bg-white/[0.03]">
                <p className="text-[10px] uppercase tracking-widest text-neutral-500">Visitas</p>
                <div className="font-display text-2xl mt-1">{metrics?.by_type?.visit || 0}</div>
              </div>
              <div className="p-4 rounded-lg bg-neutral-50 dark:bg-white/[0.03]">
                <p className="text-[10px] uppercase tracking-widest text-neutral-500">Visitantes únicos</p>
                <div className="font-display text-2xl mt-1">{metrics?.unique_visitors || 0}</div>
              </div>
              <div className="p-4 rounded-lg bg-neutral-50 dark:bg-white/[0.03]">
                <p className="text-[10px] uppercase tracking-widest text-neutral-500">Cliques totais</p>
                <div className="font-display text-2xl mt-1">{totalClicks}</div>
              </div>
              <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/50">
                <p className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400">Conversão (cliques/visitante)</p>
                <div className="font-display text-2xl mt-1 text-emerald-700 dark:text-emerald-400">{conversionRate}%</div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mt-6">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">Cliques por destino</p>
                <ul className="space-y-2">
                  {[
                    ["click_whatsapp", "WhatsApp"],
                    ["click_trial", "Teste grátis"],
                    ["click_buy", "Comprar"],
                    ["click_login", "Login"],
                    ["click_other", "Outros"],
                  ].map(([k, label]) => (
                    <li key={k} className="flex items-center justify-between text-sm">
                      <span className="text-neutral-600 dark:text-neutral-300">{label}</span>
                      <span className="font-mono">{metrics?.by_type?.[k] || 0}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">Dispositivo</p>
                <ul className="space-y-2">
                  {Object.entries(metrics?.by_device || {}).map(([k, v]) => {
                    const Icon = DEVICE_ICON[k] || Globe;
                    return (
                      <li key={k} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300"><Icon size={13}/> {k}</span>
                        <span className="font-mono">{v}</span>
                      </li>
                    );
                  })}
                  {Object.keys(metrics?.by_device || {}).length === 0 && (
                    <li className="text-sm text-neutral-400">Sem dados ainda.</li>
                  )}
                </ul>
              </div>
            </div>

            {metrics?.top_referrers?.length > 0 && (
              <div className="mt-6">
                <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-3">Top origens</p>
                <ul className="space-y-1.5 text-sm">
                  {metrics.top_referrers.map((r, i) => (
                    <li key={i} className="flex items-center justify-between gap-3">
                      <span className="truncate text-neutral-600 dark:text-neutral-300 text-xs font-mono">{r.referrer || "direto"}</span>
                      <span className="font-mono text-xs">{r.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
